import { PlayType } from '../../common/constants';
import { isManifestUrl } from '../../common/utils/sourceTypeUtils';
import { CacheDuration, MediaType } from '../../config/ProxyConfig';
import { ObjectRequest, ObjectRequestMapper } from '../interfaces';
import getCacheDuration from './getCacheDuration';
import { ApiInfo } from './interfaces';

interface DefaultOptions {
  edgeCacheBaseUrl: string;
  playType: PlayType;
  cacheDurations: CacheDuration[];
  timeout: number;
  app: string;
  sdkVer: string;
  sessionToken: string;
}

interface ApiOptions extends DefaultOptions {
  apiInfo: ApiInfo;
  tenantId: string;
  contentId: string;
}

export type MapperOptions = DefaultOptions | ApiOptions;

const getApi = ({
  edgeCacheBaseUrl,
  apiInfo,
  tenantId,
  contentId
}: {
  edgeCacheBaseUrl: string;
  apiInfo: ApiInfo;
  tenantId: string;
  contentId: string;
}): { modifiedUrl: URL; authHeader: string } => {
  const operation = apiInfo.operations.find(
    op => op.name === 'getContent' && op.version === '1.1'
  );
  if (operation) {
    const path = operation.uri
      .replace('{tenantId}', tenantId)
      .replace('{contentId}', contentId)
      // this will get updated within the mapper
      .replace('{encodedUrl}', encodeURIComponent('TO_BE_UPDATED'));
    return {
      modifiedUrl: new URL(path, edgeCacheBaseUrl),
      authHeader: 'X-Kollective-EC-Authorization'
    };
  } else {
    // if the operation is not found fall back to the default behavior
    return {
      modifiedUrl: new URL(edgeCacheBaseUrl),
      authHeader: 'Authorization'
    };
  }
};

const setCommonHeaders = (
  request: ObjectRequest,
  {
    maxAge,
    app,
    mediaType,
    sdkVer
  }: { maxAge?: number; app?: string; mediaType: MediaType; sdkVer: string }
): ObjectRequest => {
  if (maxAge) {
    request.headers.set('X-Kollective-S-Max-Age', maxAge.toString());
  }
  if (app) {
    request.headers.set('X-Kollective-AppName', app);
  }
  request.headers.set('X-Kollective-MediaType', mediaType);
  request.headers.set('X-Kollective-KSDKVersion', sdkVer);
  return request;
};

const apiMapper = (options: ApiOptions): ObjectRequestMapper => {
  const { timeout, sessionToken, sdkVer } = options;
  const { modifiedUrl, authHeader } = getApi(options);
  const setApp = options.app && options.app !== 'ksdk';

  return request => {
    const mediaType = isManifestUrl(request.url)
      ? MediaType.MANIFEST
      : MediaType.SEGMENT;

    const maxAge = getCacheDuration(
      options.playType,
      mediaType,
      options.cacheDurations
    );
    request = setCommonHeaders(request, {
      maxAge,
      mediaType,
      app: setApp ? options.app : undefined,
      sdkVer
    });

    request.headers.set('X-Kollective-ObjectId', request.objectId);
    request.headers.set(authHeader, `Bearer ${sessionToken}`);
    modifiedUrl.searchParams.set('url', request.url);
    return {
      ...request,
      modifiedUrl: modifiedUrl.toString(),
      timeout
    };
  };
};

const defaultMapper = (options: DefaultOptions): ObjectRequestMapper => {
  const { timeout, sessionToken, sdkVer } = options;
  const modifiedURL = new URL(options.edgeCacheBaseUrl);
  const setApp = options.app && options.app !== 'ksdk';

  return request => {
    const mediaType = isManifestUrl(request.url)
      ? MediaType.MANIFEST
      : MediaType.SEGMENT;

    const maxAge = getCacheDuration(
      options.playType,
      mediaType,
      options.cacheDurations
    );
    request = setCommonHeaders(request, {
      maxAge,
      mediaType,
      app: setApp ? options.app : undefined,
      sdkVer
    });
    request.headers.set('Authorization', `Bearer ${sessionToken}`);
    modifiedURL.searchParams.set('url', request.url);
    return {
      ...request,
      modifiedUrl: modifiedURL.toString(),
      timeout
    };
  };
};

export default function getRequestMapper(
  options: MapperOptions
): ObjectRequestMapper {
  const apiOptions = options as ApiOptions;
  if (apiOptions.apiInfo && apiOptions.tenantId && apiOptions.contentId) {
    return apiMapper(options as ApiOptions);
  }
  return defaultMapper(options);
}
